import { WebMercatorViewport } from '@deck.gl/core';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMostRecentLocations } from 'emergency_data/src/utils/getMostRecentLoc';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { PHASE } from '@rsos/constants/locationTypes';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import InfoWindowContent from '../../InfoWindow/InfoWindowContent';
import { StyledPopoverCaret, StyledCallPopover } from './MapPopover.styles';

const MemoizedIncidentPopover = React.memo(
  ({ x, y, data, isInternational, callQueue }) => {
    const coord = {
      x: x || 0,
      y: y || 0,
    };

    if (data?.callData?.dataType === 'call') {
      const callStatus =
        callQueue[data.callData.caller_id]?.callStatuses &&
        callQueue[data.callData.caller_id].callStatuses[
          callQueue[data.callData.caller_id].callStatuses.length - 1
        ];

      return (
        <StyledCallPopover x={coord.x} y={coord.y}>
          <InfoWindowContent
            type={EMERGENCY_CALLS}
            data={data?.callData}
            isInternational={isInternational}
            callStatus={callStatus}
          />
          <StyledPopoverCaret isGoogleMap />
        </StyledCallPopover>
      );
    }
    return <></>;
  },
);

const IncidentMapPopover = ({ overlay }) => {
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const incidentPopover = useSelector(
    state => state.mapPopover.incidentPopover,
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const hoveredID = useSelector(state => state.hoveredIncident.hoveredID);

  const incidentPopoverNormalized = useMemo(() => {
    if (incidentPopover) {
      return incidentPopover;
    } else {
      const isCallHovered = !!callQueue[hoveredID];

      if (isCallHovered) {
        const {
          mostRecentLocation,
          mostRecentSecondaryLocation,
        } = getMostRecentLocations(callQueue[hoveredID]);

        // CALLS - with ONLY secondary location
        const callWithOnlySecondaryLoc =
          !mostRecentLocation &&
          mostRecentSecondaryLocation?.class_of_service_category?.includes(
            PHASE,
          );

        if (
          callWithOnlySecondaryLoc &&
          (!selectedCallerID || selectedCallerID === hoveredID)
        ) {
          return mostRecentSecondaryLocation;
        }
        return null;
      } else {
        // ALERTS - no action for now
        return null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentPopover, hoveredID, callQueue]);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, incidentPopoverNormalized) => {
      const viewport = new WebMercatorViewport(viewState);
      if (incidentPopoverNormalized) {
        let callLocPosition = [
          incidentPopoverNormalized.longitude,
          incidentPopoverNormalized.latitude,
        ];
        const [x, y] = viewport.project(callLocPosition);

        setPopoverData({
          callData: incidentPopoverNormalized, // wrapped in {callData:...} when passing to CallPopover
          x,
          y,
        });
        setPopoverPosition({ x, y });
      }
    },
    [setPopoverPosition],
  );

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && incidentPopoverNormalized) {
      const viewState = overlay.getViewState();
      reposition(viewState, incidentPopoverNormalized);
    }

    if (!incidentPopoverNormalized) {
      setPopoverPosition(null);
      setPopoverData(null);
    }
  }, [overlay, incidentPopoverNormalized, reposition]);

  return (
    <>
      {mapProvider === GOOGLE && popoverPosition && popoverData && (
        <MemoizedIncidentPopover
          x={popoverPosition?.x}
          y={popoverPosition?.y}
          data={popoverData}
          isAllView={false}
          isInternational={false}
          callQueue={callQueue}
        />
      )}
    </>
  );
};

export default IncidentMapPopover;
